/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'
import { TIMES_OF_DAY } from '@sportninja/common/constants/timesOfDay'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import React from 'react'
import { font, paragraphSmall } from 'src/components/css'
import {
  detailSectionStyle,
  detailSectionTitleStyle,
} from './detailSectionStyles'

export interface CandidateAvailableTimesProps {
  availableTimes?: string[] | null
}

// Match case-insensitively for the same reason as CandidateAvailableDays: the
// payload may use lowercase names while TIMES_OF_DAY.value is capitalized.
const findTime = (time: string) =>
  TIMES_OF_DAY.find((t) => t.value.toLowerCase() === time.toLowerCase())

/** Available times as day-ordered chips. Renders nothing when empty. */
const CandidateAvailableTimes: React.FC<CandidateAvailableTimesProps> = ({
  availableTimes,
}) => {
  const times = availableTimes ?? []
  if (times.length === 0) {
    return null
  }
  const ordered = [...times].sort(
    (a, b) => (findTime(a)?.index ?? 0) - (findTime(b)?.index ?? 0)
  )

  return (
    <div css={detailSectionStyle}>
      <span css={detailSectionTitleStyle}>
        {capitalizeWords('Available Times')}
      </span>
      <div css={chipsStyle}>
        {ordered.map((time) => (
          <span key={time} css={chipStyle}>
            {findTime(time)?.name ?? time}
          </span>
        ))}
      </div>
    </div>
  )
}

export default CandidateAvailableTimes

const chipsStyle = css`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

/* Dark fill (matching the section panels) so the chips read as inset tokens
   rather than outlined buttons on the modal body. */
const chipStyle = css`
  ${font.body}
  ${paragraphSmall}
  padding: 4px 12px;
  border: 1px solid ${colors.SOFT_STEEL};
  border-radius: 6px;
  background-color: ${colors.SECONDARY_400};
  color: ${colors.WHITE};
`
